
import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ActionButton } from '../../components/Forms/ActionButton';
import { useCreateBrand, useGetBrandById, useUpdateBrand } from '../../queries/brand';
import { useGetActiveOrganiser } from '../../queries/user';
import ActiveStatus from '../../components/Common/ActiveStatus';
import { useEffect, useState } from 'react';
import NewDropdown from '../../components/Forms/NewDropdown';
import { useLocation, useNavigate } from 'react-router-dom';
import MediaUpload from '../../components/Forms/MediaUpload';

const CreateBrand = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const brandId = state?.brandId || null;

    const { data: brandData, isLoading: isBrandDataLoading } = useGetBrandById(brandId);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        watch,
        setValue,
        trigger,
    } = useForm({
        defaultValues: {
            organizerId: '',
            brandName: '',
            shortDescription: '',
            longDescription: '',
            remarks: '',
        }
    });

    const [active, setActive] = useState(true);
    const [logo, setLogo] = useState(null);
    const [mediaClear, setMediaClear] = useState(false);

    // Fetch data for select options
    const { data: organiserList = [] } = useGetActiveOrganiser();

    const { mutateAsync: createBrand, isPending: isCreating } = useCreateBrand();
    const { mutateAsync: updateBrand, isPending: isUpdating } = useUpdateBrand(brandId);

    const watchedOrganizerId = watch("organizerId");

    const onSubmit = (data) => {
        // Validate required fields
        if (!data.organizerId) {
            toast.error('Please select a Company/NGO');
            return;
        }

        if (!logo) {
            toast.error('Please upload a logo');
            return;
        }

        // Find the selected organizer to determine if it's Company or NGO
        const selectedOrganizer = organiserList.find(org => org._id === data.organizerId);

        const finalData = {
            isActive: active,
            brandName: data.brandName,
            shortDescription: data.shortDescription || '',
            longDescription: data.longDescription || '',
            remarks: data.remarks || '',
            file: logo || '',
        }

        // Set either companyId or ngoId based on organizer type
        if (selectedOrganizer?.organiserType === 'Company') {
            finalData.companyId = data.organizerId;
            finalData.ngoId = null;
        } else if (selectedOrganizer?.organiserType === 'NGO') {
            finalData.ngoId = data.organizerId;
            finalData.companyId = null;
        }

        console.log('Processed brand data:', finalData);

        if (brandId) {
            finalData.id = brandId;
            updateBrand(finalData, {
                onSuccess: (res) => {
                    if (res.error) {
                        console.log(res);
                        toast.error(res.error);
                    } else {
                        toast.success('Brand updated successfully!');
                        setTimeout(() => {
                            navigate('/brands/list-brand');
                        }, 2000);
                    }
                },
                onError: (error) => {
                    console.error('Error updating brand:', error);
                    toast.error('Failed to update brand. Please try again.');
                }
            });
        } else {
            createBrand(finalData, {
                onSuccess: (res) => {
                    if (res.error) {
                        console.log(res);
                        toast.error(res.error);
                    } else {
                        toast.success('Brand created successfully!');
                        reset();
                        setLogo(null);
                        setTimeout(() => {
                            navigate('/brands/list-brand');
                        }, 2000);
                    }
                },
                onError: (error) => {
                    console.error('Error creating brand:', error);
                    toast.error('Failed to create brand. Please try again.');
                }
            });
        }
    };

    useEffect(() => {
        setMediaClear(false);
    }, [logo]);

    useEffect(() => {
        setTimeout(() => {
            if (brandData) {
                setActive(brandData.isActive !== undefined ? brandData.isActive : brandData.active);

                // Handle both companyId and ngoId - one will be populated, the other will be null
                let organizerId = '';

                if (brandData.companyId) {
                    // If companyId is populated (Company sponsor)
                    organizerId = typeof brandData.companyId === 'object' && brandData.companyId?._id
                        ? brandData.companyId._id
                        : brandData.companyId;
                } else if (brandData.ngoId) {
                    // If ngoId is populated (NGO sponsor)
                    organizerId = typeof brandData.ngoId === 'object' && brandData.ngoId?._id
                        ? brandData.ngoId._id
                        : brandData.ngoId;
                }

                setValue("organizerId", organizerId);
                setValue("brandName", brandData.brandName || '');
                setValue("shortDescription", brandData.shortDescription || '');
                setValue("longDescription", brandData.longDescription || '');
                setValue("remarks", brandData.remarks || '');
                setLogo(brandData.imageUrl || brandData.logo);
            }
        }, 200);
    }, [brandData, setValue]);

    if (isBrandDataLoading || !organiserList) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-black">Loading...</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen w-full font-semibold text-base overflow-visible">
            <div className='flex items-center justify-end'>
                <ActiveStatus
                    active={active}
                    setActive={setActive}
                    isCreateForm={true}
                />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full font-semibold overflow-visible">
                <section className="flex flex-col justify-center px-5 pt-4 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] overflow-visible">

                    <div className="flex gap-6">
                        {/* Main Form Fields */}
                        <div className="flex-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                {/* Company/NGO */}
                                <div className='flex flex-col gap-1 basis-1/2'>
                                    <label htmlFor="organizerId" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                        <span className="text-red text-xs inline">* </span>
                                        Company/NGO
                                    </label>
                                    <NewDropdown
                                        name={"organizerId"}
                                        dataList={organiserList}
                                        value={watchedOrganizerId}
                                        placeholder={'Select Company/NGO'}
                                        onChange={(e) => {
                                            setValue("organizerId", e.value);
                                            if (e.value) {
                                                trigger("organizerId");
                                            }
                                        }}
                                    />
                                    {errors.organizerId && <p className="text-red text-sm mt-1">{errors.organizerId.message}</p>}
                                </div>

                                {/* Brand Name */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        <span className="text-red text-xs inline">*</span> Brand Name
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Brand Name"
                                            {...register("brandName", {
                                                required: "Brand Name is required",
                                                minLength: { value: 2, message: "Brand name must be at least 2 characters" }
                                            })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.brandName ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.brandName && <p className="text-red text-sm">{errors.brandName.message}</p>}
                                </div>

                                {/* Short Description */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        Short Description
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <textarea
                                            placeholder="Enter Short Description"
                                            {...register("shortDescription")}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[80px] resize-y ${errors.shortDescription ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.shortDescription && <p className="text-red text-sm">{errors.shortDescription.message}</p>}
                                </div>

                                {/* Long Description */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        Long Description
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <textarea
                                            placeholder="Enter Long Description"
                                            {...register("longDescription")}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.longDescription ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.longDescription && <p className="text-red text-sm">{errors.longDescription.message}</p>}
                                </div>
                            </div>

                            {/* Remarks - Full Width */}
                            <div className="flex flex-col shrink-0 basis-0 w-full mt-6">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                    Remarks
                                </label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <textarea
                                        placeholder="Enter Remarks"
                                        {...register("remarks")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[80px] resize-y ${errors.remarks ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.remarks && <p className="text-red text-sm">{errors.remarks.message}</p>}
                            </div>
                        </div>

                        {/* Logo Upload Section */}
                        <div className="flex-shrink-0">
                            <MediaUpload
                                key={logo || "new"}
                                clearMedia={mediaClear}
                                label={"Brand Logo"}
                                existingImage={logo}
                                setImageUpload={setLogo}
                                stateImage={logo}
                                setStateImageUpload={setLogo}
                            />
                        </div>
                    </div>
                    {/* Form Actions */}
                    <div className="mt-10 flex gap-5 items-center justify-between">
                        <ActionButton
                            type="button"
                            variant="reset"
                            onClick={() => {
                                reset();
                                setLogo(null);
                                setMediaClear(true);
                                setActive(true);
                            }}
                            text="Reset"
                            disabled={isSubmitting}
                        />
                        <div className="flex items-center gap-3">
                            <ActionButton
                                variant="cancel"
                                text="Cancel"
                                handleSubmit={() => navigate('/brands/list-brand')}
                            />
                            <ActionButton
                                type="submit"
                                variant="primary"
                                text={brandId ? 'Update Brand' : 'Create Brand'}
                                disabled={isCreating || isUpdating}
                            />
                        </div>
                    </div>
                </section>
            </form>
            <ToastContainer
                position="top-right"
            />
        </div>
    );
}

export default CreateBrand;